import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const LoginForm = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.username?.trim()) {
      newErrors.username = 'Username is required';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleCheckboxChange = (e) => {
    setFormData(prev => ({
      ...prev,
      rememberMe: e?.target?.checked
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    const result = await login(formData?.username, formData?.password);
    
    if (result?.success) {
      navigate('/main-menu');
    } else {
      setErrors({
        submit: result?.error || 'Login failed. Please check your credentials.'
      });
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    alert('Password recovery feature coming soon!');
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5 lg:space-y-6">
      {errors?.submit && (
        <div className="p-3 md:p-4 bg-error/10 border-2 border-error rounded-sm glitch-effect">
          <div className="flex items-start gap-2 md:gap-3">
            <Icon name="AlertTriangle" size={20} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
            <p className="text-xs md:text-sm font-caption text-error leading-relaxed">
              {errors?.submit}
            </p>
          </div>
        </div>
      )}
      <div className="space-y-4 md:space-y-5">
        <Input
          label="Username"
          type="text"
          name="username"
          placeholder="Enter your username"
          value={formData?.username}
          onChange={handleInputChange}
          error={errors?.username}
          required
          className="w-full"
        />

        <div className="relative">
          <Input
            label="Password"
            type={showPassword ? 'text' : 'password'}
            name="password"
            placeholder="Enter your password"
            value={formData?.password}
            onChange={handleInputChange}
            error={errors?.password}
            required
            className="w-full"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-9 md:top-10 p-1 hover:bg-primary/10 rounded-sm transition-all duration-base touch-target"
          >
            <Icon 
              name={showPassword ? 'EyeOff' : 'Eye'} 
              size={18} 
              color="var(--color-muted-foreground)" 
            />
          </button>
        </div>
      </div>
      <div className="flex items-center justify-between flex-wrap gap-3 md:gap-4">
        <Checkbox
          label="Remember Me"
          checked={formData?.rememberMe}
          onChange={handleCheckboxChange}
          size="sm"
        />

        <button
          type="button"
          onClick={handleForgotPassword}
          className="text-xs md:text-sm font-caption text-primary hover:text-secondary transition-colors duration-base touch-target"
        >
          Forgot Password?
        </button>
      </div>
      <Button
        type="submit"
        variant="default"
        size="lg"
        loading={isLoading}
        iconName="LogIn"
        iconPosition="right"
        fullWidth
        className="mt-6 md:mt-8"
      >
        {isLoading ? 'AUTHENTICATING...' : 'ACCESS SYSTEM'}
      </Button>
      <div className="relative my-6 md:my-8">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-primary/30" />
        </div>
        <div className="relative flex justify-center">
          <span className="px-4 text-xs md:text-sm font-caption text-muted-foreground bg-card">
            OR CONTINUE WITH
          </span>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
        <Button
          type="button"
          variant="outline"
          size="default"
          iconName="Github"
          iconPosition="left"
          fullWidth
          onClick={() => alert('Social login coming soon!')}
        >
          GitHub
        </Button>
        <Button
          type="button"
          variant="outline"
          size="default"
          iconName="Chrome"
          iconPosition="left"
          fullWidth
          onClick={() => alert('Social login coming soon!')}
        >
          Google
        </Button>
      </div>
    </form>
  );
};

export default LoginForm;